// Ta'sischilar — xsib-finance `/api/finance/stakeholders` va `/api/finance/stakeholder-distributions`.
// Summalar minor birlikda, butun son; tenantda bitta valyuta (ADR-015), shuning uchun Money
// `getCurrencyCode()` bilan quriladi. Ulush bazis punktda: 10000 = 100%.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { getCurrencyCode } from '@/shared/lib/format'
import { money, type Money } from '@/shared/lib/money'

export const STAKEHOLDER_KINDS = ['INDIVIDUAL', 'ORGANIZATION'] as const
export type StakeholderKind = (typeof STAKEHOLDER_KINDS)[number]

export const STAKEHOLDER_TYPES = ['INVESTOR', 'SHAREHOLDER', 'PARTNER', 'LENDER', 'OTHER'] as const
export type StakeholderType = (typeof STAKEHOLDER_TYPES)[number]

export const TXN_KINDS = [
  'INITIAL_INVESTMENT',
  'CAPITAL_CONTRIBUTION',
  'WITHDRAWAL',
  'ADJUSTMENT',
  'OTHER',
  'PROFIT_DISTRIBUTION',
  'LOSS_ALLOCATION',
  'PROFIT_PAYMENT',
  'ADVANCE',
  'DEBT_REPAYMENT',
  'DEBT_OFFSET',
  'REVERSAL',
] as const
export type TxnKind = (typeof TXN_KINDS)[number]
export const RECORDABLE_KINDS = ['INITIAL_INVESTMENT', 'CAPITAL_CONTRIBUTION', 'ADJUSTMENT', 'OTHER'] as const
export type RecordableKind = (typeof RECORDABLE_KINDS)[number]

export const DISTRIBUTION_STATUSES = ['CALCULATED', 'APPROVED', 'PAYABLE', 'PAID', 'CANCELLED', 'REVERSED'] as const
export type DistributionStatus = (typeof DISTRIBUTION_STATUSES)[number]

export const STATUS_COLOR: Readonly<Record<DistributionStatus, string>> = {
  CALCULATED: 'default',
  APPROVED: 'blue',
  PAYABLE: 'gold',
  PAID: 'green',
  CANCELLED: 'default',
  REVERSED: 'orange',
}

export const DISTRIBUTION_METHODS = ['AUTOMATIC', 'MANUAL'] as const
export type DistributionMethod = (typeof DISTRIBUTION_METHODS)[number]

/** LOSS — zarar ulushi: summalar musbat keladi, ta'sischi qoldig'ini kamaytiradi va to'lovga chiqmaydi. */
export const DISTRIBUTION_KINDS = ['PROFIT', 'LOSS'] as const
export type DistributionKind = (typeof DISTRIBUTION_KINDS)[number]

export const KIND_COLOR: Readonly<Record<DistributionKind, string>> = {
  PROFIT: 'green',
  LOSS: 'red',
}

export const ATTACHMENT_KINDS = ['AGREEMENT', 'CONTRACT', 'ID_DOCUMENT', 'REGISTRATION', 'BANK_DETAILS', 'OTHER'] as const
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number]
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
export const ATTACHMENT_ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', '.docx', '.xlsx'].join(',')

export const FULL_SHARE_BP = 10_000

/** Bazis punkt → foiz matni: 2000 → «20», 3333 → «33.33» */
export function bpToPercent(bp: number): number {
  return bp / 100
}

/** Foiz → bazis punkt, suzuvchi nuqta xatosisiz: 33.33 → 3333 */
export function percentToBp(percent: number): number {
  return Math.round(percent * 100)
}

const amount = (minor: number): Money => money(minor, getCurrencyCode())

const actorSchema = z.object({ id: uuidSchema, name: z.string().nullish() })
const nodeSchema = z.object({ id: uuidSchema, code: z.string().nullish(), name: z.string().nullish() })
const accountSchema = z.object({ id: uuidSchema, code: z.string(), name: z.string() })

export interface ActorRef {
  id: string
  name: string
}

export interface NodeRef {
  id: string
  code: string
  name: string
}

export interface AccountRef {
  id: string
  code: string
  name: string
}

const toActorRef = (raw: z.infer<typeof actorSchema>): ActorRef => ({ id: raw.id, name: raw.name ?? raw.id })
const toActor = (raw: z.infer<typeof actorSchema> | null | undefined): ActorRef | null => (raw ? toActorRef(raw) : null)
const toNode = (raw: z.infer<typeof nodeSchema> | null | undefined): NodeRef | null =>
  raw ? { id: raw.id, code: raw.code ?? '', name: raw.name ?? '' } : null

// --- Profil --------------------------------------------------------------------

const summarySchema = z.object({
  ownershipBp: z.number(),
  totalInvestmentMinor: z.number(),
  withdrawalsMinor: z.number(),
  adjustmentsMinor: z.number(),
  profitShareMinor: z.number(),
  profitPaidMinor: z.number(),
  capitalMinor: z.number(),
  payableMinor: z.number(),
  currentBalanceMinor: z.number(),
  initialInvestmentMinor: z.number(),
  additionalInvestmentMinor: z.number(),
  lossShareMinor: z.number(),
  withdrawnMinor: z.number(),
  debtMinor: z.number(),
  advancesMinor: z.number(),
  debtRepaidMinor: z.number(),
})

export interface StakeholderSummary {
  ownershipBp: number
  totalInvestment: Money
  withdrawals: Money
  adjustments: Money
  profitShare: Money
  profitPaid: Money
  capital: Money
  payable: Money
  /** Manfiy bo'lsa — ta'sischining kompaniyaga qarzi (`debt`) */
  currentBalance: Money
  initialInvestment: Money
  additionalInvestment: Money
  lossShare: Money
  /** Kapitaldan olingani, to'langan foyda va avans */
  withdrawn: Money
  /** Foyda/zarar qoldig'i (`payable`) manfiy bo'lsa — keyingi foydadan yoki naqd yopiladi */
  debt: Money
  advances: Money
  debtRepaid: Money
}

const toSummary = (raw: z.infer<typeof summarySchema>): StakeholderSummary => ({
  ownershipBp: raw.ownershipBp,
  totalInvestment: amount(raw.totalInvestmentMinor),
  withdrawals: amount(raw.withdrawalsMinor),
  adjustments: amount(raw.adjustmentsMinor),
  profitShare: amount(raw.profitShareMinor),
  profitPaid: amount(raw.profitPaidMinor),
  capital: amount(raw.capitalMinor),
  payable: amount(raw.payableMinor),
  currentBalance: amount(raw.currentBalanceMinor),
  initialInvestment: amount(raw.initialInvestmentMinor),
  additionalInvestment: amount(raw.additionalInvestmentMinor),
  lossShare: amount(raw.lossShareMinor),
  withdrawn: amount(raw.withdrawnMinor),
  debt: amount(raw.debtMinor),
  advances: amount(raw.advancesMinor),
  debtRepaid: amount(raw.debtRepaidMinor),
})

const rowSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  kind: z.enum(STAKEHOLDER_KINDS),
  stakeholderType: z.enum(STAKEHOLDER_TYPES),
  scope: nodeSchema.nullish(),
  startDate: z.string(),
  active: z.boolean(),
  summary: summarySchema,
})

export interface StakeholderRow {
  id: string
  code: string
  name: string
  kind: StakeholderKind
  stakeholderType: StakeholderType
  /** `null` — butun kompaniya */
  scope: NodeRef | null
  startDate: string
  active: boolean
  summary: StakeholderSummary
}

const stakeholderSchema = rowSchema.extend({
  phone: z.string().nullish(),
  whatsapp: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  idDocument: z.string().nullish(),
  bankName: z.string().nullish(),
  bankAccount: z.string().nullish(),
  paymentDetails: z.string().nullish(),
  agreementRef: z.string().nullish(),
  notes: z.string().nullish(),
  createdBy: actorSchema,
  createdAt: instantSchema,
  updatedBy: actorSchema.nullish(),
  updatedAt: instantSchema,
})

export interface Stakeholder extends StakeholderRow {
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  idDocument: string | null
  bankName: string | null
  bankAccount: string | null
  paymentDetails: string | null
  agreementRef: string | null
  notes: string | null
  createdBy: ActorRef
  createdAt: string
  updatedBy: ActorRef | null
  updatedAt: string
}

const toRow = (raw: z.infer<typeof rowSchema>): StakeholderRow => ({
  id: raw.id,
  code: raw.code,
  name: raw.name,
  kind: raw.kind,
  stakeholderType: raw.stakeholderType,
  scope: toNode(raw.scope),
  startDate: raw.startDate,
  active: raw.active,
  summary: toSummary(raw.summary),
})

const toStakeholder = (raw: z.infer<typeof stakeholderSchema>): Stakeholder => ({
  ...toRow(raw),
  phone: raw.phone ?? null,
  whatsapp: raw.whatsapp ?? null,
  email: raw.email ?? null,
  address: raw.address ?? null,
  idDocument: raw.idDocument ?? null,
  bankName: raw.bankName ?? null,
  bankAccount: raw.bankAccount ?? null,
  paymentDetails: raw.paymentDetails ?? null,
  agreementRef: raw.agreementRef ?? null,
  notes: raw.notes ?? null,
  createdBy: toActorRef(raw.createdBy),
  createdAt: raw.createdAt,
  updatedBy: toActor(raw.updatedBy),
  updatedAt: raw.updatedAt,
})

export type StatusFilter = 'ACTIVE' | 'INACTIVE' | 'ALL'

export async function fetchStakeholders(status: StatusFilter, signal?: AbortSignal): Promise<readonly StakeholderRow[]> {
  const response = await api.get<unknown>('/api/finance/stakeholders', {
    ...(signal ? { signal } : {}),
    params: { status },
  })
  return z.array(rowSchema).parse(response.data).map(toRow)
}

export async function fetchStakeholder(id: string, signal?: AbortSignal): Promise<Stakeholder> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}`, signal ? { signal } : {})
  return toStakeholder(stakeholderSchema.parse(response.data))
}

export interface StakeholderDraft {
  kind: StakeholderKind
  stakeholderType: StakeholderType
  name: string
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  idDocument: string | null
  bankName: string | null
  bankAccount: string | null
  paymentDetails: string | null
  agreementRef: string | null
  startDate: string
  notes: string | null
}

export async function createStakeholder(
  profile: StakeholderDraft,
  orgNodeId: string | null,
  shareBp: number,
): Promise<Stakeholder> {
  const response = await api.post<unknown>('/api/finance/stakeholders', { profile, orgNodeId, shareBp })
  return toStakeholder(stakeholderSchema.parse(response.data))
}

export async function updateStakeholder(id: string, profile: StakeholderDraft): Promise<Stakeholder> {
  const response = await api.put<unknown>(`/api/finance/stakeholders/${id}`, profile)
  return toStakeholder(stakeholderSchema.parse(response.data))
}

export async function setStakeholderActive(id: string, active: boolean): Promise<Stakeholder> {
  const response = await api.post<unknown>(`/api/finance/stakeholders/${id}/status`, { active })
  return toStakeholder(stakeholderSchema.parse(response.data))
}

// --- Ulush tarixi --------------------------------------------------------------

const shareSchema = z.object({
  id: uuidSchema,
  shareBp: z.number(),
  effectiveFrom: z.string(),
  reason: z.string().nullish(),
  createdBy: actorSchema,
  createdAt: instantSchema,
})

export interface ShareChange {
  id: string
  shareBp: number
  effectiveFrom: string
  reason: string | null
  createdBy: ActorRef
  createdAt: string
}

export async function fetchShares(id: string, signal?: AbortSignal): Promise<readonly ShareChange[]> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/shares`, signal ? { signal } : {})
  return z
    .array(shareSchema)
    .parse(response.data)
    .map((raw) => ({ ...raw, reason: raw.reason ?? null, createdBy: toActorRef(raw.createdBy) }))
}

export async function changeShare(
  id: string,
  body: { shareBp: number; effectiveFrom: string; reason: string },
): Promise<Stakeholder> {
  const response = await api.post<unknown>(`/api/finance/stakeholders/${id}/shares`, body)
  return toStakeholder(stakeholderSchema.parse(response.data))
}

// --- Kitob (kapital va foyda harakati) ----------------------------------------------

const txnSchema = z.object({
  id: uuidSchema,
  stakeholderId: uuidSchema,
  kind: z.enum(TXN_KINDS),
  category: z.enum(TXN_KINDS),
  amountMinor: z.number(),
  capitalMinor: z.number(),
  payableMinor: z.number(),
  txnDate: z.string(),
  account: accountSchema.nullish(),
  distributionId: uuidSchema.nullish(),
  distributionPeriodCode: z.string().nullish(),
  reversesId: uuidSchema.nullish(),
  reversedById: uuidSchema.nullish(),
  entryId: uuidSchema.nullish(),
  reference: z.string().nullish(),
  note: z.string().nullish(),
  createdBy: actorSchema,
  createdAt: instantSchema,
})

export interface LedgerTxn {
  id: string
  kind: TxnKind
  /** Storno uchun — asl yozuv turi */
  category: TxnKind
  amount: Money
  capital: Money
  payable: Money
  txnDate: string
  account: AccountRef | null
  distributionId: string | null
  periodCode: string | null
  reversesId: string | null
  reversedById: string | null
  reference: string | null
  note: string | null
  createdBy: ActorRef
  createdAt: string
}

export async function fetchLedger(id: string, signal?: AbortSignal): Promise<readonly LedgerTxn[]> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/transactions`, signal ? { signal } : {})
  return z
    .array(txnSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      kind: raw.kind,
      category: raw.category,
      amount: amount(raw.amountMinor),
      capital: amount(raw.capitalMinor),
      payable: amount(raw.payableMinor),
      txnDate: raw.txnDate,
      account: raw.account ?? null,
      distributionId: raw.distributionId ?? null,
      periodCode: raw.distributionPeriodCode ?? null,
      reversesId: raw.reversesId ?? null,
      reversedById: raw.reversedById ?? null,
      reference: raw.reference ?? null,
      note: raw.note ?? null,
      createdBy: toActorRef(raw.createdBy),
      createdAt: raw.createdAt,
    }))
}

export interface TxnDraft {
  kind: RecordableKind
  amountMinor: number
  txnDate: string
  accountId: string
  direction: 'INCREASE' | 'DECREASE' | null
  reference: string | null
  note: string | null
}

export interface PaymentDraft {
  amountMinor: number
  paidOn: string
  accountId: string
  reference: string | null
  note: string | null
}

const withKey = (key: string) => ({ headers: { 'Idempotency-Key': key } })

export async function recordTxn(id: string, draft: TxnDraft, key: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholders/${id}/transactions`, draft, withKey(key))
}

export async function recordWithdrawal(id: string, draft: PaymentDraft, key: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholders/${id}/withdrawals`, draft, withKey(key))
}

export async function recordAdvance(id: string, draft: PaymentDraft, key: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholders/${id}/advances`, draft, withKey(key))
}

export async function recordDebtRepayment(id: string, draft: PaymentDraft, key: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholders/${id}/debt-repayments`, draft, withKey(key))
}

export async function reverseTxn(id: string, txnId: string, reason: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholders/${id}/transactions/${txnId}/reverse`, { reason })
}

export async function fetchPaymentAccounts(signal?: AbortSignal): Promise<readonly AccountRef[]> {
  const response = await api.get<unknown>('/api/finance/stakeholders/payment-accounts', signal ? { signal } : {})
  return z.array(accountSchema).parse(response.data)
}

export async function fetchAdjustmentAccounts(signal?: AbortSignal): Promise<readonly AccountRef[]> {
  const response = await api.get<unknown>('/api/finance/stakeholders/adjustment-accounts', signal ? { signal } : {})
  return z.array(accountSchema).parse(response.data)
}

// --- Foyda taqsimoti ---------------------------------------------------------------

const netProfitSchema = z.object({
  revenueMinor: z.number(),
  cogsMinor: z.number(),
  operatingMinor: z.number(),
  otherMinor: z.number(),
  netMinor: z.number(),
})

export interface NetProfit {
  revenue: Money
  cogs: Money
  operating: Money
  other: Money
  net: Money
}

const toProfit = (raw: z.infer<typeof netProfitSchema>): NetProfit => ({
  revenue: amount(raw.revenueMinor),
  cogs: amount(raw.cogsMinor),
  operating: amount(raw.operatingMinor),
  other: amount(raw.otherMinor),
  net: amount(raw.netMinor),
})

const distributionSchema = z.object({
  id: uuidSchema,
  stakeholder: z.object({ id: uuidSchema, code: z.string(), name: z.string() }),
  periodId: uuidSchema,
  periodCode: z.string(),
  scope: nodeSchema.nullish(),
  method: z.enum(DISTRIBUTION_METHODS),
  netProfitMinor: z.number(),
  shareBp: z.number(),
  calculatedMinor: z.number(),
  approvedMinor: z.number().nullish(),
  paidMinor: z.number(),
  remainingMinor: z.number(),
  status: z.enum(DISTRIBUTION_STATUSES),
  note: z.string().nullish(),
  calculatedBy: actorSchema,
  calculatedAt: instantSchema,
  approvedBy: actorSchema.nullish(),
  approvedAt: instantSchema.nullish(),
  payableBy: actorSchema.nullish(),
  payableAt: instantSchema.nullish(),
  closedBy: actorSchema.nullish(),
  closedAt: instantSchema.nullish(),
  closeReason: z.string().nullish(),
  kind: z.enum(DISTRIBUTION_KINDS),
  offsetMinor: z.number(),
})

export interface Distribution {
  id: string
  stakeholder: { id: string; code: string; name: string }
  periodId: string
  periodCode: string
  scope: NodeRef | null
  method: DistributionMethod
  netProfit: Money
  shareBp: number
  calculated: Money
  approved: Money | null
  paid: Money
  remaining: Money
  status: DistributionStatus
  note: string | null
  calculatedBy: ActorRef
  calculatedAt: string
  approvedBy: ActorRef | null
  approvedAt: string | null
  payableBy: ActorRef | null
  payableAt: string | null
  closedBy: ActorRef | null
  closedAt: string | null
  closeReason: string | null
  kind: DistributionKind
  /** Ta'sischi qarziga hisoblangan qism */
  offset: Money
}

const toDistribution = (raw: z.infer<typeof distributionSchema>): Distribution => ({
  id: raw.id,
  stakeholder: raw.stakeholder,
  periodId: raw.periodId,
  periodCode: raw.periodCode,
  scope: toNode(raw.scope),
  method: raw.method,
  netProfit: amount(raw.netProfitMinor),
  shareBp: raw.shareBp,
  calculated: amount(raw.calculatedMinor),
  approved: raw.approvedMinor == null ? null : amount(raw.approvedMinor),
  paid: amount(raw.paidMinor),
  remaining: amount(raw.remainingMinor),
  status: raw.status,
  note: raw.note ?? null,
  calculatedBy: toActorRef(raw.calculatedBy),
  calculatedAt: raw.calculatedAt,
  approvedBy: toActor(raw.approvedBy),
  approvedAt: raw.approvedAt ?? null,
  payableBy: toActor(raw.payableBy),
  payableAt: raw.payableAt ?? null,
  closedBy: toActor(raw.closedBy),
  closedAt: raw.closedAt ?? null,
  closeReason: raw.closeReason ?? null,
  kind: raw.kind,
  offset: amount(raw.offsetMinor),
})

const runSchema = z.object({
  periodId: uuidSchema,
  periodCode: z.string(),
  periodState: z.string(),
  recordDate: z.string(),
  scope: nodeSchema.nullish(),
  profit: netProfitSchema.nullish(),
  totalShareBp: z.number(),
  eligibleCount: z.number(),
  rows: z.array(distributionSchema),
})

export interface DistributionRun {
  periodId: string
  periodCode: string
  closed: boolean
  recordDate: string
  scope: NodeRef | null
  /** Faqat yopilgan davrda */
  profit: NetProfit | null
  totalShareBp: number
  eligibleCount: number
  rows: readonly Distribution[]
}

const toRun = (raw: z.infer<typeof runSchema>): DistributionRun => ({
  periodId: raw.periodId,
  periodCode: raw.periodCode,
  closed: raw.periodState === 'CLOSED',
  recordDate: raw.recordDate,
  scope: toNode(raw.scope),
  profit: raw.profit ? toProfit(raw.profit) : null,
  totalShareBp: raw.totalShareBp,
  eligibleCount: raw.eligibleCount,
  rows: raw.rows.map(toDistribution),
})

export async function fetchRun(periodId: string, orgNodeId: string | null, signal?: AbortSignal): Promise<DistributionRun> {
  const response = await api.get<unknown>('/api/finance/stakeholder-distributions', {
    ...(signal ? { signal } : {}),
    params: { periodId, ...(orgNodeId ? { orgNodeId } : {}) },
  })
  return toRun(runSchema.parse(response.data))
}

export async function calculateRun(periodId: string, orgNodeId: string | null): Promise<DistributionRun> {
  const response = await api.post<unknown>('/api/finance/stakeholder-distributions/calculate', { periodId, orgNodeId })
  return toRun(runSchema.parse(response.data))
}

export async function fetchStakeholderDistributions(id: string, signal?: AbortSignal): Promise<readonly Distribution[]> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/distributions`, signal ? { signal } : {})
  return z.array(distributionSchema).parse(response.data).map(toDistribution)
}

export async function recordManualDistribution(body: {
  stakeholderId: string
  periodId: string
  approvedMinor: number
  note: string
}): Promise<void> {
  await api.post<unknown>('/api/finance/stakeholder-distributions/manual', body)
}

export async function approveDistribution(id: string, approvedMinor: number | null, note: string | null): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholder-distributions/${id}/approve`, { approvedMinor, note })
}

export async function approveDistributions(ids: readonly string[]): Promise<void> {
  await api.post<unknown>('/api/finance/stakeholder-distributions/approve', { ids })
}

export async function markDistributionPayable(id: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholder-distributions/${id}/payable`)
}

export async function payDistribution(id: string, draft: PaymentDraft, key: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholder-distributions/${id}/pay`, draft, withKey(key))
}

export async function cancelDistribution(id: string, reason: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholder-distributions/${id}/cancel`, { reason })
}

export async function reverseDistribution(id: string, reason: string): Promise<void> {
  await api.post<unknown>(`/api/finance/stakeholder-distributions/${id}/reverse`, { reason })
}

// --- Doira natijalari ---------------------------------------------------------------

const performanceSchema = z.object({
  periodId: uuidSchema,
  periodCode: z.string(),
  profit: netProfitSchema,
  shareBp: z.number(),
  expectedMinor: z.number(),
  distributionId: uuidSchema.nullish(),
  status: z.enum(DISTRIBUTION_STATUSES).nullish(),
  approvedMinor: z.number().nullish(),
  paidMinor: z.number(),
  kind: z.enum(DISTRIBUTION_KINDS),
})

export interface PerformanceRow {
  periodId: string
  periodCode: string
  profit: NetProfit
  shareBp: number
  expected: Money
  status: DistributionStatus | null
  approved: Money | null
  paid: Money
  kind: DistributionKind
}

export async function fetchPerformance(id: string, signal?: AbortSignal): Promise<readonly PerformanceRow[]> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/performance`, signal ? { signal } : {})
  return z
    .array(performanceSchema)
    .parse(response.data)
    .map((raw) => ({
      periodId: raw.periodId,
      periodCode: raw.periodCode,
      profit: toProfit(raw.profit),
      shareBp: raw.shareBp,
      expected: amount(raw.expectedMinor),
      status: raw.status ?? null,
      approved: raw.approvedMinor == null ? null : amount(raw.approvedMinor),
      paid: amount(raw.paidMinor),
      kind: raw.kind,
    }))
}

// --- Hujjatlar ----------------------------------------------------------------------

const attachmentSchema = z.object({
  id: uuidSchema,
  kind: z.enum(ATTACHMENT_KINDS),
  originalName: z.string().nullish(),
  contentType: z.string(),
  sizeBytes: z.number(),
  createdBy: uuidSchema,
  createdAt: instantSchema,
})

export interface StakeholderAttachment {
  id: string
  kind: AttachmentKind
  fileName: string | null
  sizeBytes: number
  createdAt: string
}

export async function fetchAttachments(id: string, signal?: AbortSignal): Promise<readonly StakeholderAttachment[]> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/attachments`, signal ? { signal } : {})
  return z
    .array(attachmentSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      kind: raw.kind,
      fileName: raw.originalName ?? null,
      sizeBytes: raw.sizeBytes,
      createdAt: raw.createdAt,
    }))
}

export async function uploadAttachment(id: string, kind: AttachmentKind, file: File): Promise<void> {
  const body = new FormData()
  body.append('file', file)
  body.append('kind', kind)
  await api.post<unknown>(`/api/finance/stakeholders/${id}/attachments`, body, {
    headers: { 'Content-Type': undefined },
  })
}

export async function attachmentUrl(id: string, attachmentId: string): Promise<string> {
  const response = await api.get<unknown>(`/api/finance/stakeholders/${id}/attachments/${attachmentId}/url`)
  return z.object({ url: z.string() }).parse(response.data).url
}

export async function deleteAttachment(id: string, attachmentId: string): Promise<void> {
  await api.delete<unknown>(`/api/finance/stakeholders/${id}/attachments/${attachmentId}`)
}
